import {
  useAsyncCall,
  AsyncCallArgs,
  GridContainer,
  GridItem,
  SubmitFormButton,
  handleFormSubmission,
  useReactHookForm,
  AsyncAutocompleteWithAdd,
  InputFormField,
  FormSection,
  IsActiveFilter,
} from "@duosoftbg/nacid-components";
import { Typography } from "@mui/material";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useAppDispatch from "../../../../../../../hooks/redux/base/useAppDispatch";
import { toast } from "react-toastify";
import {
  createHomePageCategoryLink,
  getRolesWithAdditionalParamsAutocomplete,
  updateHomePageCategoryLink,
} from "../../../../../../../axios/api/services";
import React, { useEffect, useState } from "react";
import { LinkDetails } from "../../../../../../../types/homePageCategoryTypes";
import { addCategoryViewObject } from "../../HomePageCategoryManagement";
import ScrollableGridContainer from "../../../../../../components/common/grid/ScrollableGridContainer";
import AccessRolesDialogList from "../AccessRolesDialogList";
import { createCategoryLinkValidationSchema } from "../../../../../../../yup/schema/homePageCategory/homePageCategoryLinkValidationSchema";

const EditCategoryLinkDialogBody = ({ setOpen, isEditMode, link }) => {
  const { t } = useTranslation();
  const { asyncCall } = useAsyncCall();
  const dispatch = useAppDispatch();
  const [roles, setRoles] = useState([]);

  const { methods } = useReactHookForm<LinkDetails>({
    defaultValues: link,
    validationSchema: createCategoryLinkValidationSchema,
  });

  useEffect(() => {
    setRoles(link.accessRoles);
  }, [link]);

  const submit = (values) => {
    const asyncSaveGroup: AsyncCallArgs = {
      withGlobalBackdrop: true,
      promise: values.id !== "" ? updateHomePageCategoryLink(values) : createHomePageCategoryLink(values),
      reactHooksForm: { methods },
      onSuccess: (response) => {
        setOpen(false);
        addCategoryViewObject(response, dispatch);
        toast.success(t("m.save.data.success"));
      },
    };
    asyncCall(asyncSaveGroup);
  };

  const handleAddRoleToUser = (role) => {
    const accessRoles = methods.getValues("accessRoles");
    if (!accessRoles.find((arrayRole) => arrayRole === role.name)) {
      methods.setValue("accessRoles", [...accessRoles, role.name]);
      setRoles([...accessRoles, role.name]);
    }
  };

  const handleRemoveRoleClick = (role) => {
    const newRoles = roles.filter((existingRole) => existingRole !== role);
    methods.setValue("accessRoles", [...newRoles]);
    setRoles([...newRoles]);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleFormSubmission({ methods, onSubmitFn: submit, t })}>
        <FormSection label={"t.main.details"}>
          <GridContainer spacing={3} mt={1}>
            <IsActiveFilter sm={4} md={4} fieldName={"active"} />
            <GridItem sm={4} md={4}>
              <InputFormField fieldName={"order"} labelCode={"l.input.home.category.position"} />
            </GridItem>
            <GridItem sm={12} md={12}>
              <InputFormField fieldName={"name"} labelCode={"l.description"} />
            </GridItem>

            <GridItem sm={12} md={12}>
              <InputFormField fieldName={"nameEn"} labelCode={"l.description.en"} />
            </GridItem>
            <GridItem sm={12} md={12}>
              <InputFormField fieldName={"href"} labelCode={"l.input.category.link"} />
            </GridItem>
          </GridContainer>
        </FormSection>
        <FormSection label={"t.user.roles"}>
          <GridContainer spacing={3} mt={1}>
            <AsyncAutocompleteWithAdd
              key={`roles-autocomplete-key-${methods.getValues("accessRoles").length}`}
              onAddRecord={handleAddRoleToUser}
              setOptionText={(option) => option.name}
              getOptionLabel={(option) => option.id}
              setInputOnSelect={(option) => option.name}
              autocompleteFn={getRolesWithAdditionalParamsAutocomplete}
              label={"l.select.role"}
              autocompletePropsGrid={{ sm: 12, md: 11.5 }}
            />
            <ScrollableGridContainer>
              <AccessRolesDialogList roles={roles} isEdit={true} onRemove={handleRemoveRoleClick} />
            </ScrollableGridContainer>
          </GridContainer>
        </FormSection>
        <GridContainer spacing={2}>
          <GridItem sm={12} md={12}>
            <Typography align={"right"}>
              <SubmitFormButton label={"l.btn.save"} />
            </Typography>
          </GridItem>
        </GridContainer>
      </form>
    </FormProvider>
  );
};

export default EditCategoryLinkDialogBody;
